import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { ArrowLeftRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { GET_TRANSACTIONS } from "../graphql/transactions";
import { TransactionList } from "../components/TransactionList";
import { TransactionSkeleton } from "../components/SkeletonLoader";
import { GradientHeader } from "../components/GradientHeader";
import { SectionCard } from "../components/SectionCard";
import { PageContainer } from "../components/PageContainer";
import { enableBackButton } from "../lib/telegram";

export const Transactions = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offset, setOffset] = useState(0);
  const limit = 20;

  useEffect(() => {
    return enableBackButton(() => navigate("/"));
  }, [navigate]);

  const { data, loading, fetchMore } = useQuery(GET_TRANSACTIONS, {
    variables: { userId: user?.id, limit, offset: 0 },
    skip: !user?.id,
  });

  const transactions = data?.transactions || [];
  const totalCount = data?.transactions_aggregate?.aggregate?.count || 0;
  const totalSpent = data?.transactions_aggregate?.aggregate?.sum?.amount || 0;
  const hasMore = transactions.length < totalCount;

  const loadMore = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    fetchMore({
      variables: { offset: newOffset },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          transactions: [...prev.transactions, ...fetchMoreResult.transactions],
        };
      },
    });
  };

  return (
    <PageContainer>
      <GradientHeader size="sm">
        <p className="font-display text-2xl font-semibold text-ink tracking-[-0.02em]">
          Activity
        </p>
        {totalCount > 0 && (
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted mt-0.5">
            {totalCount} transaction{totalCount !== 1 ? "s" : ""}
          </p>
        )}
      </GradientHeader>

      {/* Stats pills */}
      {totalCount > 0 && (
        <div className="flex gap-3 px-4 mt-4">
          <div className="bg-surface rounded-xl border border-hairline px-4 py-2.5 flex-1">
            <p className="font-mono text-[10px] text-muted uppercase tracking-[0.1em]">Count</p>
            <p className="money text-sm font-semibold text-ink">{totalCount}</p>
          </div>
          <div className="bg-surface rounded-xl border border-hairline px-4 py-2.5 flex-1">
            <p className="font-mono text-[10px] text-muted uppercase tracking-[0.1em]">Total spent</p>
            <p className="money text-sm font-semibold text-ink">
              ${Number(totalSpent).toFixed(2)}
            </p>
          </div>
        </div>
      )}

      <div className="px-4 mt-4">
        <SectionCard>
          {loading ? (
            <div className="pt-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <TransactionSkeleton key={i} />
              ))}
            </div>
          ) : transactions.length > 0 ? (
            <>
              <TransactionList transactions={transactions} />
              {hasMore && (
                <button
                  onClick={loadMore}
                  className="w-full py-3 text-accent text-sm font-semibold"
                >
                  Load more
                </button>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center text-center py-10">
              <div className="w-16 h-16 rounded-xl bg-ground border border-hairline flex items-center justify-center mb-4">
                <ArrowLeftRight size={28} className="text-muted/60" />
              </div>
              <p className="font-medium text-ink">No transactions yet</p>
              <p className="text-xs text-muted mt-1.5 max-w-[220px]">
                Once you start using your cards, all transactions will appear here
              </p>
            </div>
          )}
        </SectionCard>
      </div>
    </PageContainer>
  );
};
